import React from "react";
import navbarLogo from "./assets/letsgosauna-navbar.svg";
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from "react-router-dom";
import { nextParam } from "./utils/authRedirect";
import { ApolloProvider, useQuery, gql } from "@apollo/client";
import client from "./apolloClient";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./components/Auth/Login";
import Signup from "./components/Auth/Signup";
import Home from "./pages/Home";
import BookingDetail from "./pages/BookingDetail";
import ReviewWizard from "./pages/ReviewWizard";
import Dashboard from "./pages/Dashboard";
import ProfileSetup from "./pages/ProfileSetup";
import SaunaCreateWizard from "./pages/SaunaCreateWizard";
import SaunaEdit from "./pages/SaunaEdit";
import SaunaDetail from "./pages/SaunaDetail";
import Messages from "./pages/Messages";
import MessageThread from "./pages/MessageThread";
import Docs from "./pages/Docs";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Checkout from "./pages/Checkout";
import AdminBookings from "./pages/AdminBookings";
import OnboardingModal from "./components/OnboardingModal";
import { useIsMobile } from "./hooks/useIsMobile";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

const ME_QUERY = gql`
  query NavMe($userId: Int!) {
    userById(id: $userId) {
      phone
      email
      userProfileByUserId { fullName }
    }
  }
`;

const UNREAD_COUNT_QUERY = gql`
  query MyUnreadCount {
    myUnreadCount
  }
`;

const Nav = () => {
  const { user, userId, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const isMobile = useIsMobile();
  const np = nextParam(location);

  const { data } = useQuery(ME_QUERY, {
    variables: { userId },
    skip: !userId,
  });
  const { data: unreadData } = useQuery(UNREAD_COUNT_QUERY, {
    skip: !userId,
    fetchPolicy: "network-only",
  });

  const profile = data?.userById?.userProfileByUserId;
  const phone   = data?.userById?.phone ?? "";
  const email   = data?.userById?.email ?? "";
  const displayName = profile?.fullName || phone || email || "User";
  const unreadCount = unreadData?.myUnreadCount ?? 0;

  const closeMenu = () => setMenuOpen(false);

  if (isMobile) {
    return (
      <nav style={{ ...navStyle, position: "relative", flexWrap: "wrap", padding: "12px 16px" }}>
        <Link to="/" onClick={closeMenu} style={linkStyle}><img src={navbarLogo} alt="Lets Go Sauna" style={{ height: 44, display: "block" }} /></Link>
        <button
          onClick={() => setMenuOpen(o => !o)}
          style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", padding: 4, fontSize: 20, lineHeight: 1 }}
          aria-label="Menu"
        >
          {menuOpen ? "✕" : "☰"}
        </button>
        {menuOpen && (
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
            background: "#fff", borderBottom: "1px solid #e5e7eb",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            display: "flex", flexDirection: "column", padding: "8px 0",
          }}>
            {user ? (
              <>
                {displayName && (
                  <span style={{ padding: "10px 20px", fontSize: 14, color: "#374151", borderBottom: "1px solid #f3f4f6" }}>
                    Welcome, <strong>{displayName}</strong>
                  </span>
                )}
                <Link to="/" onClick={closeMenu} style={mobileMenuLink}>Browse</Link>
                <Link to="/docs" onClick={closeMenu} style={mobileMenuLink}>Docs</Link>
                <Link to="/privacy" onClick={closeMenu} style={mobileMenuLink}>Privacy</Link>
                <Link to="/terms" onClick={closeMenu} style={mobileMenuLink}>Terms</Link>
                <Link to="/dashboard" onClick={closeMenu} style={mobileMenuLink}>Dashboard</Link>
                <Link to="/messages" onClick={closeMenu} style={{ ...mobileMenuLink, display: "flex", alignItems: "center", gap: 6 }}>
                  Messages
                  {unreadCount > 0 && <span style={unreadBadge}>{unreadCount}</span>}
                </Link>
                <button
                  onClick={() => { logout(); navigate("/"); closeMenu(); }}
                  style={{ margin: "8px 16px", padding: "8px 14px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14, textAlign: "left" }}
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/" onClick={closeMenu} style={mobileMenuLink}>Browse</Link>
                <Link to="/docs" onClick={closeMenu} style={mobileMenuLink}>Docs</Link>
                <Link to="/privacy" onClick={closeMenu} style={mobileMenuLink}>Privacy</Link>
                <Link to="/terms" onClick={closeMenu} style={mobileMenuLink}>Terms</Link>
                <div style={{ display: "flex", gap: 10, padding: "12px 20px 4px" }}>
                  <Link to={`/login${np}`} onClick={closeMenu} style={{ ...authBtnStyle, flex: 1, textAlign: "center" }}>Log in</Link>
                  <Link to={`/signup${np}`} onClick={closeMenu} style={{ ...authBtnStyle, flex: 1, textAlign: "center", background: "#2563eb", color: "#fff", border: "1px solid #2563eb" }}>Sign up</Link>
                </div>
              </>
            )}
          </div>
        )}
      </nav>
    );
  }

  return (
    <nav style={navStyle}>
      <Link to="/" style={linkStyle}><img src={navbarLogo} alt="Lets Go Sauna" style={{ height: 44, display: "block" }} /></Link>
      {user ? (
        <>
          <Link to="/" style={linkStyle}>Browse</Link>
          <Link to="/docs" style={linkStyle}>Docs</Link>
          <Link to="/privacy" style={linkStyle}>Privacy</Link>
          <Link to="/terms" style={linkStyle}>Terms</Link>
          <Link to="/dashboard" style={linkStyle}>Dashboard</Link>
          <Link to="/messages" style={{ ...linkStyle, position: "relative" }}>
            Messages
            {unreadCount > 0 && <span style={unreadBadge}>{unreadCount}</span>}
          </Link>
          {displayName && (
            <span style={{ marginLeft: "auto", fontSize: 14, color: "#374151" }}>
              Welcome, <strong>{displayName}</strong>
            </span>
          )}
          <button onClick={() => { logout(); navigate("/"); }} style={{ ...logoutStyle, marginLeft: displayName ? 12 : "auto" }}>
            Log out
          </button>
        </>
      ) : (
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          <Link to="/docs" style={linkStyle}>Docs</Link>
          <Link to="/privacy" style={linkStyle}>Privacy</Link>
          <Link to="/terms" style={linkStyle}>Terms</Link>
          <Link to={`/login${np}`} style={authBtnStyle}>Log in</Link>
          <Link to={`/signup${np}`} style={{ ...authBtnStyle, background: "#2563eb", color: "#fff", border: "1px solid #2563eb" }}>Sign up</Link>
        </div>
      )}
    </nav>
  );
};

const AuthGuard = ({ fallback }: { fallback: string }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  const next = new URLSearchParams(location.search).get("next");
  return <Navigate to={next || fallback} replace />;
};

const AppRoutes = () => {
  const { user, userId } = useAuth();
  const [onboardingDismissed, setOnboardingDismissed] = React.useState(false);
  const { data: meData, loading: meLoading } = useQuery(ME_QUERY, {
    variables: { userId },
    skip: !userId,
  });
  const hasProfile = !!meData?.userById?.userProfileByUserId?.fullName;
  const showOnboarding = !!user && !!userId && !meLoading && !hasProfile && !onboardingDismissed;

  return (
    <>
      <Nav />
      {showOnboarding && (
        <OnboardingModal userId={userId!} phone={meData?.userById?.phone} onDismiss={() => setOnboardingDismissed(true)} />
      )}
      <Routes>
        {/* Public */}
        <Route path="/login" element={user ? <AuthGuard fallback="/" /> : <Login />} />
        <Route path="/signup" element={user ? <AuthGuard fallback="/" /> : <Signup />} />

        {/* Publicly browsable */}
        <Route path="/" element={<Home />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/listings" element={<Navigate to="/" replace />} />
        <Route path="/saunas/:id" element={<SaunaDetail />} />
        <Route path="/ops-967cea124678bc60" element={<AdminBookings />} />

        {/* Protected */}
        <Route path="/bookings/:id" element={<ProtectedRoute><BookingDetail /></ProtectedRoute>} />
        <Route path="/bookings/:id/review" element={<ProtectedRoute><ReviewWizard /></ProtectedRoute>} />
        <Route path="/checkout/:bookingId" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        <Route path="/messages/:threadId" element={<ProtectedRoute><MessageThread /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/profile/setup" element={<ProtectedRoute><ProfileSetup /></ProtectedRoute>} />
        <Route path="/saunas/new" element={<ProtectedRoute><SaunaCreateWizard /></ProtectedRoute>} />
        <Route path="/saunas/:id/edit" element={<ProtectedRoute><SaunaEdit /></ProtectedRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ApolloProvider>
  );
}

const navStyle: React.CSSProperties = {
  padding: "12px 24px", borderBottom: "1px solid #e5e7eb",
  display: "flex", gap: 24, alignItems: "center",
};
const linkStyle: React.CSSProperties = { color: "#2563eb", textDecoration: "none", fontSize: 14 };
const authBtnStyle: React.CSSProperties = {
  padding: "6px 14px", border: "1px solid #d1d5db", borderRadius: 6,
  fontSize: 14, cursor: "pointer", textDecoration: "none", color: "#374151",
};
const logoutStyle: React.CSSProperties = {
  marginLeft: "auto", padding: "6px 14px", background: "#ef4444",
  color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14,
};
const mobileMenuLink: React.CSSProperties = {
  display: "block", padding: "10px 20px", color: "#2563eb", textDecoration: "none", fontSize: 14,
};
const unreadBadge: React.CSSProperties = {
  position: "absolute", top: -6, right: -12,
  minWidth: 16, height: 16, borderRadius: 8, padding: "0 4px",
  background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 700,
  display: "flex", alignItems: "center", justifyContent: "center",
};

export default App;
